import { useRef, useState, useMemo } from 'react'
import {
  Box, Button, Card, CardBody, CardHeader, Checkbox, FormControl, FormLabel,
  Heading, HStack, IconButton, Input, InputGroup, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, Textarea, VStack, useDisclosure,
} from '@chakra-ui/react'
import { Plus, Search, Trash2 } from 'lucide-react'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

const BASE_PATH = '/service-catalog/fe-checklists'

type ChecklistItem = { label: string; sort_order: number; is_required: boolean }

export default function FeChecklistsPage() {
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { showError, showSuccess } = useToastNotification()
  const access = useAccessCheck()
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [active, setActive] = useState(true)
  const [items, setItems] = useState<ChecklistItem[]>([{ label: '', sort_order: 0, is_required: true }])
  const [saving, setSaving] = useState(false)

  const tableOptions: DataTableOptions = useMemo(() => ({
    dataFields: {
      name: { name: 'Name', withSort: true, maxWidth: '240px' },
      items_count: { name: 'Items', withSort: true, maxWidth: '80px' },
      active_label: { name: 'Status', withSort: true, maxWidth: '100px', badge: { Active: 'green', Inactive: 'yellow' } },
      updated_at: { name: 'Updated', withSort: true, maxWidth: '150px' },
    },
    dataSource: BASE_PATH,
    primaryKey: 'id',
    softDelete: false,
    displayInModal: false,
    showActionsColumn: true,
    noRecordText: 'No checklist templates found',
  }), [])

  const openCreate = () => {
    setEditingId(null)
    setName('')
    setActive(true)
    setItems([{ label: '', sort_order: 0, is_required: true }])
    onOpen()
  }

  const openEdit = async (id: number) => {
    try {
      const res = await apiClient.get(`${BASE_PATH}/${id}`)
      const d = res.data?.data ?? res.data ?? {}
      setEditingId(id)
      setName(d.name ?? '')
      setActive(d.active !== false)
      setItems((d.items ?? []).map((item: ChecklistItem, i: number) => ({
        label: item.label ?? '',
        sort_order: item.sort_order ?? i,
        is_required: Boolean(item.is_required),
      })))
      onOpen()
    } catch (e) {
      showError(e)
    }
  }

  const handleSave = async () => {
    if (!name.trim()) return showError('Name is required')
    setSaving(true)
    try {
      const payload = { name, active, items: items.filter((i) => i.label.trim()) }
      if (editingId) {
        await apiClient.put(`${BASE_PATH}/${editingId}`, payload)
        showSuccess('Checklist updated')
      } else {
        await apiClient.post(BASE_PATH, payload)
        showSuccess('Checklist created')
      }
      tableRef.current?.reload()
      onClose()
    } catch (e) {
      showError(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box p={4}>
      <Card>
        <CardHeader>
          <HStack justify="space-between">
            <Box>
              <Heading size="md">FE Checklist Builder</Heading>
              <Text fontSize="sm" color={colors.text}>Manage field engineer checklist templates</Text>
            </Box>
            {access.can_create && <Button leftIcon={<Plus size={16} />} colorScheme="blue" onClick={openCreate}>Create Checklist</Button>}
          </HStack>
        </CardHeader>
        <CardBody>
          <InputGroup maxW="320px" mb={4} size="sm">
            <Input ref={searchRef} placeholder="Search..." onKeyDown={(e) => e.key === 'Enter' && setSearchQuery(searchRef.current?.value || '')} />
            <Button ml={2} size="sm" onClick={() => setSearchQuery(searchRef.current?.value || '')} leftIcon={<Search size={14} />}>Search</Button>
          </InputGroup>
          <DataTable
            ref={tableRef}
            options={tableOptions}
            params={{ search: searchQuery }}
            access={{ can_edit: access.can_edit, can_delete: access.can_delete, can_create: access.can_create, can_view: access.can_view, can_export: false, can_import: false }}
            setSubSub={(all) => setTotalRows(all)}
            onOpenModal={(id) => void openEdit(Number(id))}
          />
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay /><ModalContent>
          <ModalHeader>{editingId ? 'Edit Checklist' : 'Create Checklist'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired><FormLabel>Name</FormLabel><Input value={name} onChange={(e) => setName(e.target.value)} /></FormControl>
              <FormControl><Checkbox isChecked={active} onChange={(e) => setActive(e.target.checked)}>Active</Checkbox></FormControl>
              <Text fontWeight="semibold">Items</Text>
              {items.map((item, index) => (
                <HStack key={index} align="flex-start">
                  <Input flex={1} placeholder="Item label" value={item.label} onChange={(e) => {
                    const next = [...items]; next[index] = { ...next[index], label: e.target.value }; setItems(next)
                  }} />
                  <Checkbox isChecked={item.is_required} onChange={(e) => {
                    const next = [...items]; next[index] = { ...next[index], is_required: e.target.checked }; setItems(next)
                  }}>Required</Checkbox>
                  <IconButton aria-label="Remove item" icon={<Trash2 size={14} />} size="sm" onClick={() => setItems(items.filter((_, i) => i !== index))} />
                </HStack>
              ))}
              <Button size="sm" variant="outline" leftIcon={<Plus size={14} />} alignSelf="flex-start" onClick={() => setItems([...items, { label: '', sort_order: items.length, is_required: false }])}>Add item</Button>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" isLoading={saving} onClick={() => void handleSave()}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
